import { useEffect, useState } from "react"

import { ContentObjectItem } from "@vertesia/common"
import { Atom, FileCode, FileJson, FileSpreadsheet, FileText, FileVideo, Image } from "lucide-react"

import { Badge } from "@ui/badge"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@ui/tooltip"
import { isImage, isJson, isMsFile, isOfficeFile, isMarkdown, isPdf, isPlainText, isTextMarkdown, isVideo } from "@/lib/mime"
import { retrieveRendition } from "@/lib/renditions"
import { useUserSession } from "@session/UserSession"

interface DocumentIconProps {
    doc: ContentObjectItem
    displayScore?: boolean
}
export function DocumentIcon({ doc, displayScore = false }: Readonly<DocumentIconProps>) {
    const { client } = useUserSession()

    const title = doc?.properties?.title || doc.name || doc.id
    const [renditionUrl, setRenditionUrl] = useState<string | undefined>(undefined)
    const [renditionAlt, setRenditionAlt] = useState<string | undefined>(undefined)

    const score = displayScore ? (doc as any)?.score : undefined

    useEffect(() => {
        if (!doc?.content) {
            return
        }

        retrieveRendition(client, doc, setRenditionUrl, setRenditionAlt)
    }, [doc])

    return (
        <div className="flex flex-col h-fit rounded-sm border border-gray-300 bg-white hover:cursor-pointer hover:shadow-xl transition-shadow overflow-hidden relative">
            {score && (
                <Badge className="absolute top-2 right-2 z-10 bg-blue-600 hover:bg-blue-500 text-white shadow-md">
                    {score?.toFixed(2)}
                </Badge>
            )}
            {renditionUrl ? (
                <div className="h-48 overflow-hidden rounded-t-sm">
                    <img src={renditionUrl} alt={renditionAlt} className="w-full h-full object-cover transform scale-100 hover:scale-110 transition-all duration-300" />
                </div>
            ) : (
                <div className="h-48 bg-gray-700 rounded-t-sm overflow-hidden">
                    <div className="h-full transform scale-100 hover:scale-110 transition-all duration-300">
                        {getContentPlaceholder(doc?.content?.type)}
                    </div>
                </div>
            )}
            <div className="p-4 flex flex-col gap-1 border-t border-gray-300">
                <div className="flex flex-col">
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger>
                                <h3 className="pb-0.5 text-start font-medium leading-none truncate">{title}</h3>
                            </TooltipTrigger>
                            <TooltipContent>
                                <div className="text-sm">{title}</div>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger>
                                <p className="text-start text-xs text-muted-foreground truncate">{doc?.type?.name}</p>
                            </TooltipTrigger>
                            <TooltipContent>
                                <div className="text-sm">{doc?.type?.name}</div>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            </div>
        </div>
    )
}

function getContentPlaceholder(type: string | undefined) {
    if (!type) {
        return (
            <div className="h-full flex items-center justify-center">
                <Atom className='text-white size-16' />
            </div>
        )
    }

    return (
        <div className="h-full flex items-center justify-center">
            {isImage(type) && <Image className='text-white size-16' />}
            {isJson(type) && <FileJson className='text-white size-16' />}
            {(isPlainText(type) || isPdf(type)) && <FileText className='text-white size-16' />}
            {(isMsFile(type) || isOfficeFile(type)) && <FileSpreadsheet className='text-white size-16' />}
            {isVideo(type) && <FileVideo className='text-white size-16' />}
            {(isMarkdown(type) || isTextMarkdown(type)) && <FileCode className='text-white size-16' />}
        </div>
    )
}